import { S25Const } from "../../util/s25-const";
import { Item } from "../../pojo/Item";
import { OptUtil } from "../s25-opt/s25.opt.util";
import { S25Util } from "../../util/s25-util";
import { ContactService } from "../../services/contact.service";
import { FlsService } from "../../services/fls.service";
import { UserprefService } from "../../services/userpref.service";
import { PreferenceService } from "../../services/preference.service";
import { CacheRepository } from "../../decorators/cache.decorator";
import { AccessLevels, Fls, hasFullFls, isMinFls } from "../../pojo/Fls";
import { S25Help } from "../s25-help/s25.help.service";
import { ContactI } from "../../pojo/ContactI";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { Debounce } from "../../decorators/debounce.decorator";
import { Contact } from "../../pojo/Contact";
import { S25ObjectDetailsGeneralComponent } from "./general-details/s25.object.details.general.component";
import { S25ContactDetailsUserAccountComponent } from "./contact-details/user-account/s25.contact.details.user.account";
import { S25ObjectDetailsEditableTextComponent } from "./s25.object.details.editable.text.component";
import { S25EditableContactOrgsComponent } from "./contact-details/editable-orgs/s25.editable.contact.orgs.component";
import { S25MasqueradeRolesComponent } from "../S25-masquerade/masquerade.roles.component";
import { S25ObjectDetailsAddressComponent } from "./address/s25.object.details.address.component";
import { Proto } from "../../pojo/Proto";
import { S25ObjectDetailsCustomAttrComponent } from "./custom-attrs/s25.object.details.custom.attr.component";
import { OrganizationService, UpdatedContactRoles } from "../../services/organization.service";
import { ObjectPermissionService } from "../../services/object.permission.service";
import { OrganizationI } from "../../pojo/OrganizationI";
import { ObjectDetailsUtil } from "../../util/object.details.util";
import { AddressType } from "../../pojo/AddressI";
import { S25EditablePropertyComponent } from "../s25-editable-property/s25.editable.property.component";
import { S25NotificationPolicyComponent } from "../s25-notification-policy/s25.notification.policy.component";
import { S25OrgPartitionPrefsComponent } from "./organization-details/s25.org.partition.prefs.component";
import { SearchService } from "../../services/search/search.service";
import { S25OrgPartitionPrefsService } from "./organization-details/s25.org.partition.prefs.service";
import NumericalBoolean = Proto.NumericalBoolean;
import { S25OrgContactRolesComponent } from "./organization-details/s25.org.contact.roles.component";
import { Space, SpaceService } from "../../services/space.service";
import { Location } from "../../pojo/Location";
import Feature = Location.Feature;
import { Attribute } from "../s25-event/EventMicroI";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { EventService } from "../../services/event.service";
import { ResourceService } from "../../services/resource.service";
import { S25LocationDetailsHoursComponent } from "./location-details/s25.location.details.hours.component";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25LocationDetailsLayoutsComponent } from "./location-details/s25.location.details.layouts.component";
import { S25ObjectDetailsRelationshipsComponent } from "./s25.object.details.relationships.component";
import Relationship = Location.Relationship;
import { S25ObjectDetailsItemPlainComponent } from "./s25.object.details.item.plain.component";
import { BuildingService } from "../../services/building.service";
import { S25EChartsComponent } from "../s25-charts/s25.echarts.component";
import { S25LocationDetailsLayoutDetailsComponent } from "./location-details/s25.location.details.layout.details.component";
import { Injectable, signal } from "@angular/core";
import Layout = Location.Layout;
import { S25GoogleMapComponent } from "../../standalone/s25.google.maps.component";

@Injectable()
export class S25ObjectDetailsService {
    readonly selectedLayout = signal<Layout>(null);
    readonly layouts = signal<Layout[]>([]);

    setSelectedLayout(selectedLayout: Layout) {
        this.selectedLayout.set(selectedLayout);
    }

    setLayouts(layouts: Layout[]) {
        this.layouts.set(layouts);
    }

    onLayoutChange(layout: Layout) {
        this.layouts.update((prev) => {
            const newLayouts = prev.filter((l) => l.layoutId !== layout.layoutId);
            return [...newLayouts, layout];
        });
        this.selectedLayout.set(layout);
    }

    setSelectedLayoutById(id: number) {
        this.selectedLayout.set(this.layouts().find((layout) => layout.layoutId === id) ?? null);
    }

    @Debounce(500)
    public static async getObjDetailsData(itemId: number, itemTypeId: Item.Id) {
        switch (itemTypeId) {
            case Item.Ids.Contact:
                return S25ObjectDetailsService.getContactDetailsData(itemId);
            case Item.Ids.Organization:
                return S25ObjectDetailsService.getOrganizationDetailsData(itemId);
            case Item.Ids.Location:
                return S25ObjectDetailsService.getLocationDetailsData(itemId);
        }
    }

    private static getSearchQuery(itemId: number, itemTypeId: Item.Id): string {
        return `&${S25Const.typeId2ItemIdB[itemTypeId]}=${itemId}`;
    }

    private static async getLocationDetailsData(itemId: number) {
        const [detailResp, canEdit, flsResp, isLoggedIn, is24HourResp, dateTimePrefResp, editPref] =
            await Promise.allSettled([
                SpaceService.getSpace(itemId),
                ObjectPermissionService.canEdit(itemId, Item.Ids.Location),
                FlsService.getFls(),
                UserprefService.getLoggedIn(),
                UserprefService.getIs24HourTime(),
                UserprefService.getS25DateTimeformat(),
                PreferenceService.getPreferences(["SpaceShowInlineEdit", "Eval25"]),
            ]);
        const detailData = detailResp.status === "fulfilled" && detailResp.value,
            spaceDetails = detailData?.data?.items?.[0],
            hasEditPerms =
                canEdit.status === "fulfilled" &&
                canEdit.value &&
                editPref.status === "fulfilled" &&
                editPref.value.SpaceShowInlineEdit?.value === "T",
            eval25Enabled = editPref.status === "fulfilled" && editPref.value.Eval25?.value === "true",
            is24Hour = is24HourResp.status === "fulfilled" && is24HourResp.value,
            dateTimePref = dateTimePrefResp.status === "fulfilled" && dateTimePrefResp.value,
            fls = flsResp.status === "fulfilled" && flsResp.value,
            formatData = await S25ObjectDetailsService.formatLocationDetailsData(detailData, fls);
        return {
            panels: [
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            data: spaceDetails?.comments ?? "",
                            prop: "comments",
                        },
                    },
                },
                {
                    header: "Default Instructions",
                    id: "instructions",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            data: spaceDetails?.instructions ?? "",
                            prop: "instructions",
                        },
                    },
                },
                {
                    header: "Features",
                    id: "features",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Location,
                                canEdit: hasEditPerms,
                                properties: formatData.features,
                                propType: "locationFeatures",
                                propPayloadType: "feature",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: formatData.attributes,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Hours",
                    id: "hours",
                    section: 0,
                    sectionOrder: 5,
                    content: {
                        component: S25LocationDetailsHoursComponent,
                        inputs: {
                            hours: spaceDetails?.hours,
                            itemId: itemId,
                            canEdit: hasEditPerms,
                            is24Hour: is24Hour,
                        },
                    },
                },
                {
                    header: "Layouts",
                    id: "layouts",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25LocationDetailsLayoutsComponent,
                        inputs: {
                            layouts: formatData.layouts,
                            itemId: itemId,
                            canEdit: hasEditPerms,
                            maxCapacity: spaceDetails?.maxCapacity,
                        },
                    },
                },
                {
                    header: "Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Location,
                                canEdit: hasEditPerms,
                                properties: formatData.categories,
                                propType: "locationCategories",
                                propPayloadType: "category",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Related Spaces",
                    id: "related",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsRelationshipsComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            relationships: formatData.relationships,
                        },
                    },
                },
                {
                    header: "Location Scheduler",
                    id: "scheduler",
                    section: 1,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "scheduler",
                            canEdit: hasEditPerms,
                            value:
                                spaceDetails?.roles?.map((role) => ({
                                    itemId: role.contactId,
                                    itemTypeId: Item.Ids.Contact,
                                    itemName: formatData?.schedulerName || S25Const.private,
                                    primaryId: role.roleId,
                                }))?.[0] ?? {},
                        },
                    },
                },
                {
                    header: "Partition",
                    id: "partition",
                    section: 1,
                    sectionOrder: 5,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "partition",
                            canEdit: hasEditPerms,
                            value: formatData?.partition,
                        },
                    },
                },
                {
                    header: "Fill Ratio Percentage",
                    id: "ratio",
                    section: 1,
                    sectionOrder: 6,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "ratio",
                            canEdit: hasEditPerms,
                            value: spaceDetails?.fillRatio,
                        },
                    },
                },
                ...(hasEditPerms
                    ? [
                          {
                              header: "Express",
                              id: "express",
                              section: 1,
                              sectionOrder: 7,
                              content: {
                                  component: S25ObjectDetailsItemPlainComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Location,
                                      type: "express",
                                      canEdit: hasEditPerms,
                                      value:
                                          spaceDetails?.dirSched?.map((schedule) => ({
                                              itemId: schedule.directScheduleTypeId,
                                              itemName: schedule.directScheduleTypeName,
                                          }))?.[0] ?? {},
                                      altVal: S25Util.minutesToISODuration(
                                          spaceDetails?.dirSched?.[0]?.directScheduleDuration ?? 0,
                                      ),
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Building",
                    id: "building",
                    section: 1,
                    sectionOrder: 8,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "building",
                            canEdit: hasEditPerms,
                            value: formatData?.building,
                            altVal: {
                                imageId: (formatData?.building as any)?.imageId,
                                imageUrl: (formatData?.building as any)?.url,
                            },
                        },
                    },
                },
                ...(spaceDetails?.roomRating?.length && eval25Enabled
                    ? [
                          {
                              header: "Location Satisfaction",
                              id: "eval25Rating",
                              section: 2,
                              sectionOrder: 1,
                              content: {
                                  component: S25EChartsComponent,
                                  inputs: {
                                      roomData: spaceDetails.roomRating.sort(
                                          (a, b) => +new Date(a.endDt) - +new Date(b.endDt),
                                      ),
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Layout Details",
                    id: "layoutDetails",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25LocationDetailsLayoutDetailsComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Map",
                    id: "map",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25GoogleMapComponent,
                        inputs: {
                            latitude: formatData.coordinates?.latitude,
                            longitude: formatData.coordinates?.longitude,
                            zoom: 15,
                        },
                    },
                },
                ...(isLoggedIn && isMinFls(fls.SPACE_NOTIFY, AccessLevels.Full)
                    ? [
                          {
                              header: "Notification Policy",
                              id: "notPolicy",
                              section: 2,
                              sectionOrder: 4,
                              content: {
                                  component: S25NotificationPolicyComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Location,
                                      type: "location",
                                      inModal: false,
                                      canEdit: hasEditPerms,
                                      editModeOff: !hasEditPerms,
                                  },
                              },
                          },
                      ]
                    : []),
            ],
            dateStamp: S25Datefilter.transform(S25Util.date.currentDateTime(), dateTimePref),
        };
    }

    private static async formatLocationDetailsData(
        detailData: {
            data: { items: [Space] };
            expandedInfo: Partial<Space>;
        },
        fls: Fls,
    ) {
        const { features, attributes, layouts, categories, relationships, buildingId, roles } =
            detailData?.data?.items?.[0] ?? {};
        const {
            features: featureDetails,
            attributes: attributeDetails,
            layouts: layoutDetails,
            categories: categoryDetails,
            partition,
            building,
            contacts,
        } = detailData?.expandedInfo ?? {};
        const hasContactPerms = isMinFls(fls.CU_CONTACT, AccessLevels.Read);
        const entries =
            (attributeDetails as Attribute[])
                ?.filter((attr) => !!Number(attr.dataType))
                ?.map((attr) => {
                    const attribute = attributes.find((attrData) => attrData.attributeId === attr.attributeId);
                    const key = `${attribute.value}-${attr.dataType}`;
                    const promise = S25ObjectDetailsService.getObjNameMap[+attr.dataType](attribute.value);
                    return [key, promise];
                }) ?? [];
        const objNames = await Promise.allSettled(entries.map(([_, promise]) => promise));
        const objData = Object.fromEntries(
            entries.map(([key]: string[], index: number) => [
                key,
                objNames[index].status === "fulfilled" ? objNames[index].value : S25Const.private,
            ]),
        );
        const buildingImageData = !!buildingId && (await BuildingService.getImageUrl(buildingId));
        //ANG-6308 contacts service requires auth and we should already have the info we need
        let schedulerName;
        if (hasContactPerms && roles?.length) {
            const schedulerContId = roles?.[0]?.contactId;
            const contact = S25Util.array.forceArray(contacts).find((c) => c.contactId === schedulerContId);
            if (contact) {
                schedulerName = `${contact.firstName} ${contact.familyName}`;
            } else {
                const isLoggedIn = await UserprefService.getLoggedIn();
                schedulerName = isLoggedIn ? await ContactService.getContactName(roles[0].contactId) : S25Const.private;
            }
        }

        return {
            features:
                features
                    ?.map((feature) => {
                        const featureObj: Feature = featureDetails.find(
                            (featureData: Feature) => featureData.featureId === feature.featureId,
                        );
                        return {
                            itemId: feature.featureId,
                            itemName: featureObj?.featureName ?? "",
                            quantity: feature.quantity,
                        };
                    })
                    ?.sort((a, b) => a.itemName.localeCompare(b.itemName, undefined, { sensitivity: "base" })) ?? [],
            attributes:
                attributes
                    ?.map((attr) => {
                        const attrObj = attributeDetails?.find(
                                (attrData) => attrData.attributeId === attr.attributeId,
                            ) as Attribute,
                            attrType = Number(attrObj?.dataType) || attrObj?.dataType;
                        return {
                            custAttrId: attr.attributeId,
                            itemId:
                                typeof attrType === "number" || attrType === CustomAttributes.type.Image
                                    ? attr.value
                                    : attr.attributeId,
                            imageId: attrType === CustomAttributes.type.Image ? attr.value : null,
                            itemName:
                                typeof attrType === "number" && !!objData
                                    ? objData[`${attr.value}-${attrType}`]
                                    : attr.value,
                            itemLabel: attrObj?.attributeName ?? "",
                            itemTypeId: attrType,
                        };
                    })
                    ?.sort((a, b) => a.itemLabel.localeCompare(b.itemLabel, undefined, { sensitivity: "base" })) ?? [],
            layouts:
                layouts
                    ?.map((layout) => {
                        const layoutObj = layoutDetails?.find((layoutData) => layoutData.layoutId === layout.layoutId);
                        return { ...layout, layoutName: layoutObj?.layoutName ?? "" };
                    })
                    ?.sort((a, b) => a.layoutName.localeCompare(b.layoutName, undefined, { sensitivity: "base" })) ??
                [],
            categories:
                categories
                    ?.map((category) => {
                        const catObj = categoryDetails?.find((catData) => catData.categoryId === category.categoryId);
                        return {
                            itemId: category.categoryId,
                            itemName: catObj?.categoryName ?? "",
                        };
                    })
                    ?.sort((a, b) => a.itemName.localeCompare(b.itemName, undefined, { sensitivity: "base" })) ?? [],
            relationships: Object.entries(relationships ?? {}) //process so an array of type name and list of associated spaces, ["Subdivision Of", "subdivisionOf", [{itemId: 123}]]
                .filter(([_, spaces]) => !!spaces.length)
                .map(([relType, spaces]) => {
                    const titleCaseRelType = relType
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())
                        .replace(/\b\w/g, (char) => char.toUpperCase());
                    return [
                        titleCaseRelType,
                        relType,
                        spaces.map((space: Relationship) => ({
                            itemId: space.spaceId,
                        })),
                    ];
                }),
            partition: { itemId: partition?.[0]?.partitionId, itemName: partition?.[0]?.partitionName ?? "" },
            building:
                building?.map((building) => ({
                    itemId: building.buildingId,
                    itemName: building.buildingName ?? "",
                    ...(!!buildingImageData && {
                        imageId: buildingImageData?.imageId,
                        imageUrl: buildingImageData?.url,
                    }),
                }))?.[0] ?? {},
            coordinates: {
                latitude: +attributes?.find((attr) => attr.attributeId === -14)?.value,
                longitude: +attributes?.find((attr) => attr.attributeId === -15)?.value,
            },
            schedulerName: schedulerName,
        };
    }

    private static async getOrganizationDetailsData(itemId: number) {
        const [detailData, fls, isLoggedIn, canEdit, editPref] = await Promise.all([
            OrganizationService.getOrganization(itemId),
            FlsService.getFls(),
            UserprefService.getLoggedIn(),
            ObjectPermissionService.canEdit(itemId, Item.Ids.Organization),
            PreferenceService.getPreferences(["AcctShowInlineEdit"]),
        ]);
        const hasEditPerms = canEdit && editPref?.AcctShowInlineEdit?.value === "T";
        const orgDetails = await S25ObjectDetailsService.formatOrgDetailsData(
            detailData,
            fls,
            hasEditPerms,
            isLoggedIn,
        );
        return {
            panels: [
                {
                    header: "General",
                    id: "general",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsGeneralComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            model: orgDetails.general,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                            data: orgDetails.comments.comments,
                            canEditComments: orgDetails.comments.canEditComments,
                            prop: "comments",
                        },
                    },
                },
                {
                    header: "Administrative Address",
                    id: "adminAddr",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: orgDetails.editableAdminAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Billing Address",
                    id: "billingAddr",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: orgDetails.editableBillingAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Organization,
                                canEdit: hasEditPerms,
                                properties: orgDetails.categories,
                                propType: "organizationCategories",
                                propPayloadType: "category",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: orgDetails.attributes,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                ...(isLoggedIn && isMinFls(fls.CU_ACCT_NOTIFY, AccessLevels.Read)
                    ? [
                          {
                              header: "Notification Policy",
                              id: "notPolicy",
                              section: 1,
                              sectionOrder: 3,
                              content: {
                                  component: S25NotificationPolicyComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Organization,
                                      type: "organization",
                                      inModal: false,
                                      canEdit: hasEditPerms && isMinFls(fls.CU_ACCT_NOTIFY, AccessLevels.Edit),
                                      editModeOff: !hasEditPerms,
                                  },
                              },
                          },
                      ]
                    : []),
                ...(isLoggedIn
                    ? [
                          {
                              header: "Partition Preferences",
                              id: "partitionPrefs",
                              section: 2,
                              sectionOrder: 1,
                              content: {
                                  component: S25OrgPartitionPrefsComponent,
                                  inputs: {
                                      modelBean: { ...orgDetails.partitionPrefs, orgId: itemId },
                                      readOnly: !hasEditPerms || !isMinFls(fls.CU_ACCT_PREF, AccessLevels.Edit),
                                      doSave: true,
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Associated Contacts",
                    id: "contacts",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25OrgContactRolesComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: hasEditPerms && isMinFls(fls.CU_CONTACT, AccessLevels.Read),
                            contacts: (detailData?.contact ?? []).map(OrganizationService.toContactRoleObj),
                            onChange: (primaryContactId: number, updatedContacts: UpdatedContactRoles) =>
                                OrganizationService.updateContactRoles([itemId], {
                                    ...updatedContacts,
                                    primaryId: primaryContactId,
                                }),
                        },
                    },
                },
            ],
        };
    }

    private static async formatOrgDetailsData(
        detailData: OrganizationI,
        fls: Fls,
        canEdit: boolean,
        isLoggedIn: boolean,
    ) {
        const prefQueryId = detailData?.space_preference?.query_id,
            adminAddress = S25Util.extractAddress(detailData.address, AddressType.Admin),
            billingAddress = S25Util.extractAddress(detailData.address, AddressType.Billing);
        let partitionSteps;
        if (isLoggedIn && prefQueryId) {
            const [data, error] = await S25Util.Maybe(SearchService.getSearchCriteria(Item.Ids.Location, prefQueryId));
            if (error) console.error(error);
            partitionSteps = data;
        }
        let orgType = S25Util.array.forceArray(detailData.organization_type ?? []).map((org) => ({
                itemId: org.type_id,
                itemName: org.type_name,
                isActive: S25Util.toBool(org.type_defn_state),
            })),
            rating = [{ itemName: detailData.rating_name, itemId: detailData.rating_id }],
            category = detailData.category ?? [],
            attribute = detailData.custom_attribute ?? [];
        const [types, ratings, categories, attributes] = await Promise.all([
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationTypes", orgType),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationRatings", rating),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationCategories", category, "category_id"),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationCustomAttributes", attribute, "attribute_id"),
        ]);
        orgType = types;
        rating = ratings;
        category = categories;
        attribute = attributes;
        return {
            general: {
                canEditAcctCode: canEdit && hasFullFls(fls.CU_ACCT_NUMBERS),
                canEditRating: canEdit && isMinFls(fls.CU_ACCT_RATING, AccessLevels.Edit),
                type: orgType[0],
                origType: orgType[0],
                rating: rating[0],
                orgNumber: detailData.organization_number,
            },
            comments: {
                canEditComments: canEdit && isMinFls(fls.CU_COMMENTS, AccessLevels.Edit),
                comments: S25Util.s25CleanupHtml(detailData.comments ?? ""),
            },
            editableAdminAddress: {
                street: `${adminAddress.line1 ?? ""}${adminAddress.line2 ? " " + adminAddress.line2 : ""}`,
                city: adminAddress.city ?? "",
                state: adminAddress.state ?? "",
                zip: adminAddress.zip ?? "",
                country: adminAddress.country ?? "",
                phone: adminAddress.phone ?? "",
                fax: adminAddress.fax ?? "",
                addressType: AddressType.Admin,
            },
            editableBillingAddress: {
                street: `${billingAddress.line1 ?? ""}${billingAddress.line2 ? " " + billingAddress.line2 : ""}`,
                city: billingAddress.city ?? "",
                state: billingAddress.state ?? "",
                zip: billingAddress.zip ?? "",
                country: billingAddress.country ?? "",
                phone: billingAddress.phone ?? "",
                fax: billingAddress.fax ?? "",
                addressType: AddressType.Billing,
            },
            categories: category,
            attributes: attribute.map((item) => CustomAttributeService.toCustAttrObj(item)),
            partitionPrefs: {
                groups: (S25Util.propertyGet(partitionSteps, "step") ?? [])
                    .sort(S25Util.shallowSort("step_priority", true))
                    .map(S25OrgPartitionPrefsService.stepToGroup),
                queryId: prefQueryId,
            },
        };
    }

    private static async getContactDetailsData(itemId: number) {
        const [detailData, flsResp, groupId, editPref] = await Promise.allSettled([
            ContactService.getContact(itemId),
            FlsService.getFls(),
            UserprefService.getGroupId(),
            PreferenceService.getPreferences(["ContShowInlineEdit"]),
        ]);
        const showInlineEdit = editPref.status === "fulfilled" && editPref.value.ContShowInlineEdit?.value === "T",
            fls = flsResp.status === "fulfilled" && flsResp.value,
            fullPerms = hasFullFls(fls?.CU_CONTACT),
            contactDetails = (detailData.status === "fulfilled" && detailData.value.contacts?.contact) ?? {},
            isAdmin = groupId.status === "fulfilled" && groupId.value === -1;
        let canEdit = fullPerms && showInlineEdit,
            canEditSecurity = hasFullFls(fls?.SECURITY) && showInlineEdit,
            username,
            warningMessage;
        const { r25user } = contactDetails;
        if (r25user && canEdit) {
            const systemUsers = ["service25", "object25"];
            username = r25user?.r25_username;
            const isSystemUser = systemUsers.includes(username.toLowerCase());
            const isInternal = username.toLowerCase() === "viewer25";
            canEdit = canEdit && !isSystemUser && (isAdmin || !isInternal);
            canEditSecurity = canEditSecurity && canEdit && !isInternal;
            warningMessage = S25ObjectDetailsService.setContactWarningAlert(isAdmin, isSystemUser, isInternal);
        }
        const details = S25ObjectDetailsService.formatContactDetailsData(contactDetails);
        return {
            warningMessage: warningMessage,
            panels: [
                {
                    header: "General",
                    id: "general",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsGeneralComponent,
                        inputs: {
                            model: details?.general,
                            itemTypeId: Item.Ids.Contact,
                            itemId: itemId,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "User Account",
                    id: "account",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ContactDetailsUserAccountComponent,
                        inputs: {
                            userData: details?.userAccount,
                            canEdit: canEdit,
                            canEditSecurity: canEditSecurity,
                            itemId: itemId,
                            isAdmin: isAdmin,
                        },
                    },
                },
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                            data: details?.comments,
                            prop: "comments",
                        },
                    },
                },
                {
                    header: "Associated Organizations",
                    id: "assocOrgs",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25EditableContactOrgsComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            orgs: details?.editableOrgs,
                        },
                    },
                },
                {
                    header: "Additional Roles",
                    id: "roles",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25MasqueradeRolesComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            groupId: groupId,
                        },
                    },
                },
                {
                    header: "Work Contact Details",
                    id: "workDetails",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details?.editableWorkAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Home Contact Details",
                    id: "homeDetails",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details?.editableHomeAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 1,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: details?.attributesEditable,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
            ],
        };
    }

    private static setContactWarningAlert(isAdmin: boolean, isSystemUser: boolean, isInternalUser: boolean) {
        let message, link;
        if (isSystemUser) message = "This is a system defined contact record and cannot be edited.";
        else if (isInternalUser) {
            if (isAdmin) link = S25Help.getHelpUrl("system_users");
            const viewer25Warning = "Modifying the viewer25 account can have unintended consequences.";
            const adminOnly = "This is an internal contact, and can only be edited by an administrator.";
            message = isAdmin ? viewer25Warning : adminOnly;
        }
        if (message) return { message, link };
        return null;
    }

    private static formatContactDetailsData(contact: ContactI): Contact.DetailData {
        const {
            contact_name,
            first_name,
            last_name,
            middle_name,
            name_prefix,
            name_suffix,
            title,
            comments,
            internal_id,
            r25user,
            attribute,
            organization,
        } = contact;
        const attributes = S25Util.array.forceArray(attribute ?? []),
            organizations = S25Util.array.forceArray(organization ?? []),
            homeDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "4"),
            workDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "3");
        const {
            street_address: homeStreet,
            city: homeCity,
            state_prov: homeState,
            zip_post: homeZip,
            country: homeCountry,
            fax: homeFax,
            phone: homePhone,
            email: homeEmail,
        } = homeDetails || {};
        const {
            street_address: workStreet,
            city: workCity,
            state_prov: workState,
            zip_post: workZip,
            country: workCountry,
            fax: workFax,
            phone: workPhone,
            email: workEmail,
        } = workDetails || {};
        return {
            general: {
                contactName: contact_name,
                firstName: first_name,
                middleName: middle_name,
                lastName: last_name,
                namePrefix: name_prefix,
                nameSuffix: name_suffix,
                title: title,
                internalId: internal_id,
            },
            userAccount: {
                userName: r25user?.r25_username,
                active: r25user?.r25_defn_state as NumericalBoolean,
                securityGroup: {
                    itemName: r25user?.security_group?.security_group_name,
                    itemId: r25user?.security_group?.security_group_id,
                    itemTypeId: Item.Ids.Contact,
                },
            },
            comments: comments,
            attributesEditable: attributes.map((item) => CustomAttributeService.toCustAttrObj(item)),
            editableOrgs: organizations.map((org) => ({
                itemName: org.organization_name,
                itemId: org.organization_id,
                role: { itemId: org.role_id, itemName: org.role_name },
            })),
            editableHomeAddress: {
                street: homeStreet ?? "",
                city: homeCity ?? "",
                state: homeState ?? "",
                zip: homeZip ?? "",
                country: homeCountry ?? "",
                phone: homePhone ?? "",
                fax: homeFax ?? "",
                email: homeEmail ?? "",
                addressType: AddressType.Home,
            },
            editableWorkAddress: {
                street: workStreet ?? "",
                city: workCity ?? "",
                state: workState ?? "",
                zip: workZip ?? "",
                country: workCountry ?? "",
                phone: workPhone ?? "",
                fax: workFax ?? "",
                email: workEmail ?? "",
                addressType: AddressType.Work,
            },
        };
    }

    public static invalidateCache(itemTypeId: Item.Id) {
        return CacheRepository.invalidateByMethod(S25ObjectDetailsService.ObjMap[itemTypeId].invalidateMethod);
    }

    public static getOptBean(itemId: number, itemTypeId: Item.Id) {
        const optParams = {
            itemId: itemId,
            itemTypeId: itemTypeId,
            searchContext: S25ObjectDetailsService.getSearchQuery(itemId, itemTypeId),
        };
        return S25ObjectDetailsService.ObjMap[itemTypeId].getOptBean(null, optParams);
    }

    public static ObjMap: Partial<Record<Item.Id, ObjMapItem>> = {
        [Item.Ids.Contact]: {
            invalidateMethod: "ContactService.getContact",
            getOptBean: OptUtil.formContactDetailsBean,
        },
        [Item.Ids.Organization]: {
            invalidateMethod: "OrganizationService.getOrganization",
            getOptBean: OptUtil.formOrganizationDetailsBean,
        },
        [Item.Ids.Location]: {
            invalidateMethod: "SpaceService.getSpace",
            getOptBean: OptUtil.formLocationDetailsBean,
        },
    };

    private static getObjNameMap: Record<number, any> = {
        [Item.Ids.Event]: EventService.getEventName,
        [Item.Ids.Contact]: ContactService.getContactName,
        [Item.Ids.Organization]: OrganizationService.getOrganizationName,
        [Item.Ids.Location]: SpaceService.getSpaceName,
        [Item.Ids.Resource]: ResourceService.getResourceName,
    };

    public static PayloadPropConst: Record<ItemPlainType, PropObj> = {
        partition: { idProp: "partitionId" },
        ratio: { idProp: "fillRatio" },
        scheduler: {
            typeProp: "roles",
            idProp: "roleId",
            altProp: "contactId",
            hasAltVal: true,
        },
        express: {
            typeProp: "directSchedule",
            idProp: "eventTypeId",
            altProp: "maxDuration",
            hasAltVal: true,
        },
        building: { idProp: "buildingId" },
    };
}

export type ItemPlainType = "scheduler" | "partition" | "ratio" | "express" | "building";
export type PropObj = {
    idProp: string;
    typeProp?: string;
    altProp?: string;
    hasAltVal?: boolean;
};

type ObjMapItem = {
    invalidateMethod: string;
    getOptBean: (refreshF: any, runTimeParams: any) => Promise<any>;
};
